import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const FAQS = [
  {
    q: "What does 'at our cost' actually mean?",
    a: "Whichever structure we work under — promotion or option — we fund every step of the planning process ourselves: site investigation, technical surveys, planning consultancy, community engagement, legal fees and (where relevant) remediation strategy. If we don't achieve consent, you owe us nothing.",
  },
  {
    q: "How long does the planning process usually take?",
    a: "Strategic land is patient work. Most sites take between three and eight years from initial engagement to a sale with consent. We are built for the long game and will keep you informed at every milestone.",
  },
  {
    q: "What happens if we don't get planning consent?",
    a: "You retain full ownership of your land and we absorb all costs incurred. There is no debt, no clawback and no obligation to continue with us beyond the agreement term. This protection applies under both promotion and option agreements.",
  },
  {
    q: "Will my neighbours or tenants find out?",
    a: "All landowner discussions are strictly confidential. Public consultation only happens at the formal planning stage — at which point we lead the messaging carefully and professionally.",
  },
  {
    q: "Do you work with brownfield and commercial sites too?",
    a: "Yes. Brownfield regeneration is a core specialism — including industrial, ex-employment and contaminated land. We also handle commercial uses, BTR, PRS, logistics and mixed-use schemes.",
  },
  {
    q: "How much will I receive when the site is sold?",
    a: "It depends on which structure suits your circumstances. Under an option agreement you receive a fixed, pre-agreed price (typically expressed as a discount to open-market-with-consent value). Under a promotion agreement you receive a share of the actual sale proceeds after agreed promotion costs. We'll walk you through both, in plain English, before anything is signed — and recommend the structure that best fits your objectives.",
  },
  {
    q: "Do you always work under a promotion agreement?",
    a: "No. We work under both promotion agreements and option agreements — and, where appropriate, hybrid structures such as an option with an overage clause or a promotion agreement with a minimum guaranteed sum. The right choice depends on your site, your timeline and your appetite for market exposure. Our first job is to understand what you want to achieve.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="faq-section" className="bg-[#0B0F0E] border-y border-[#F4EFE6]/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="overline"><span className="h-line" />Questions, answered</div>
          <h2 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-6 leading-[0.95]">
            What landowners <span className="italic-gold">always ask.</span>
          </h2>
          <p className="text-[#9BA39B] mt-7 max-w-md leading-relaxed">
            Honest answers to the questions that come up most often. If yours isn&apos;t here, ask us directly.
          </p>
        </div>
        <div className="lg:col-span-8 divide-y divide-[#F4EFE6]/10 border-y border-[#F4EFE6]/10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`faq-${i}`} className="py-6">
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 text-left group"
              >
                <span className="font-display text-2xl md:text-3xl text-[#F4EFE6] group-hover:text-[#C9A24B] transition-colors">{f.q}</span>
                {open === i ? <Minus size={20} className="text-[#C9A24B] shrink-0" /> : <Plus size={20} className="text-[#C9A24B] shrink-0" />}
              </button>
              {open === i && (
                <p data-testid={`faq-answer-${i}`} className="text-[#9BA39B] mt-4 leading-relaxed max-w-3xl">{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
